// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AddressSection } from './AddressSection';
import { VirtualizedList } from '@/components/shared/VirtualizedList';

import { FormSelect } from '@/components/shared/FormSelect';
import {
  Home,
  Box,
  MapPin,
  Eye,
  AlertCircle,
  Link2,
  Layers,
} from 'lucide-react'; // Icons for visual distinction
import { ASSET_CONFIG } from '@/types/asset-config';
import { LayerDefinition } from '@packages/database/types';
import { getPrimaryLabelField } from '@/helpers/layerSchema.helpers';
import { cn } from '@/lib/utils';

interface NormalizedItem {
  id: string;
  label: string;
  subLabel: string;
  type: 'asset' | 'premise';
  color?: string;
  raw: any;
}

export const LinkedAssetsSection = ({
  selectedProjectAssets = [] as any,
  selectedProjectPremises = [] as any,
  projectTypeLayers = [] as LayerDefinition[],
}: {
  selectedProjectAssets: any;
  selectedProjectPremises: any;
  projectTypeLayers: LayerDefinition[];
}) => {
  const [openAssetList, setOpenAssetList] = useState(false);
  const { control, watch, setValue, ...methods } = useFormContext();
  const [showPreview, setShowPreview] = useState(false);

  // Watch form values
  const linkingMode = watch('linkingMode');
  const linkedItems = watch('linkedItems');
  const assetTypeFilter = watch('assetTypeFilter');

  const prevModeRef = useRef<string | null>(linkingMode);

  const findLayerForAsset = (assetType: string): LayerDefinition | null =>
    projectTypeLayers.find(
      (l) =>
        l.sourceTable === 'assets' &&
        (l.sourceFilter as any)?.assetType === assetType,
    ) ?? null;

  // Combine assets and premises into a single normalized list for the Single Select Modal
  const allItems = useMemo<NormalizedItem[]>(() => {
    const assets = selectedProjectAssets.map((a: any) => {
      const layer = findLayerForAsset(a.asset.assetType);

      // Find layer schema for label extraction

      const labelField = getPrimaryLabelField(layer?.schemaConfig);

      const label = labelField
        ? String(
            a.asset[labelField.alias] ??
              a.asset[labelField.column] ??
              'Unknown',
          )
        : (a.asset.structureNo ?? a.asset.id);

      return {
        id: a.asset.id,
        label: label,
        subLabel: layer?.displayName ?? a.asset.assetType,
        color: layer?.styles?.color,
        type: 'asset' as const,
        raw: a,
      };
    });

    const premises = selectedProjectPremises.map((p: any) => {
      const layer = findLayerForAsset('PREMISE');
      const label = p.premise.streetName ?? 'Unknown Address';

      return {
        id: p.premise.id,
        label: p.premise.streetName ?? 'Unknown Address',
        subLabel: layer?.displayName ?? p.premise.type ?? 'Premise',
        color: layer?.styles?.color,
        type: 'premise' as const,
        raw: p,
      };
    });

    return [...assets, ...premises];
  }, [selectedProjectAssets, selectedProjectPremises, projectTypeLayers]);

  const hasAnyItems = allItems.length > 0;

  const filterOptions = useMemo(() => {
    const opts: { label: string; value: string }[] = [
      { label: 'All Items', value: 'ALL' },
    ];

    // Asset type layers — deduplicated by assetType slug
    const seen = new Set<string>();
    projectTypeLayers
      .filter((l) => l.sourceTable === 'assets')
      .forEach((l) => {
        const slug = (l.sourceFilter as any)?.assetType;
        if (!slug || seen.has(slug)) return;
        seen.add(slug);
        opts.push({ label: l.displayName, value: slug });
      });

    if (selectedProjectPremises.length > 0) {
      opts.push({ label: 'Premises', value: 'premise' });
    }

    return opts;
  }, [projectTypeLayers, selectedProjectPremises]);

  // ---------------------------------------------------------------------------
  //  Filter Logic (Multi Mode)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const isModeChange = prevModeRef.current !== linkingMode;

    if (linkingMode === 'multi') {
      const currentFilters: string[] = Array.isArray(assetTypeFilter)
        ? assetTypeFilter
        : assetTypeFilter
          ? [assetTypeFilter]
          : [];

      const includeAll =
        currentFilters.length === 0 || currentFilters.includes('ALL');

      const matchedAssets = selectedProjectAssets
        .filter((a) => includeAll || currentFilters.includes(a.asset.assetType))
        .map((a) => ({ id: a.asset.id, type: 'asset' as const }));

      const matchedPremises =
        includeAll || currentFilters.includes('premise')
          ? selectedProjectPremises.map((p) => ({
              id: p.premise.id,
              type: 'premise' as const,
            }))
          : [];

      setValue('linkedItems', [...matchedAssets, ...matchedPremises], {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else if (
      isModeChange &&
      (linkingMode === 'single' || linkingMode === 'adhoc')
    ) {
      // Clear stale selections on mode switch
      setValue('linkedItems', [], { shouldDirty: true, shouldValidate: true });
    }

    prevModeRef.current = linkingMode;
  }, [
    linkingMode,
    assetTypeFilter,
    selectedProjectAssets,
    selectedProjectPremises,
    setValue,
  ]);

  // =========================================================================
  // CLEAR STALE AD-HOC SELECTION when project changes (items become available)
  // =========================================================================

  useEffect(() => {
    if (hasAnyItems && linkingMode === 'adhoc') {
      setValue('linkingMode', 'single', { shouldDirty: true });
      setValue('linkedItems', [], { shouldDirty: true });
    }
  }, [hasAnyItems]);

  // =========================================================================
  // HANDLERS
  // =========================================================================

  const handleSingleSelection = (selectedId: string) => {
    if (!selectedId) {
      setValue('linkedItems', []);
      return;
    }
    const item = allItems.find((i) => i.id === selectedId);
    if (item) {
      setValue('linkedItems', [{ id: item.id, type: item.type }], {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const previewItems = useMemo(() => {
    if (linkingMode !== 'multi') return [];
    const ids = new Set(linkedItems?.map((i: any) => i.id));
    return allItems.filter((i) => ids.has(i.id));
  }, [allItems, linkedItems, linkingMode]);

  const selectedSingleItem = useMemo(() => {
    if (linkingMode !== 'single' || linkedItems?.length !== 1) return null;
    return allItems.find((i) => i.id === linkedItems[0].id) ?? null;
  }, [allItems, linkedItems, linkingMode]);

  const renderListItem = (item: NormalizedItem) => (
    <div className="flex items-center gap-3 px-3 py-2.5">
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
        style={
          item.color
            ? {
                backgroundColor: `${item.color}20`,
                border: `1px solid ${item.color}40`,
              }
            : undefined
        }
      >
        {item.type === 'premise' ? (
          <Home className="w-3.5 h-3.5 text-blue-500" />
        ) : (
          <Box
            className="w-3.5 h-3.5"
            style={{ color: item.color ?? '#f97316' }}
          />
        )}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground truncate">
          {item.label}
        </p>
        <Badge
          variant="outline"
          className="mt-0.5 text-[10px] h-4 px-1.5 font-normal"
        >
          {item.subLabel}
        </Badge>
      </div>
    </div>
  );

  return (
    <section className="w-full max-w-2xl mx-auto space-y-5 py-2">
      <div>
        <h3 className="text-base font-semibold text-foreground">Link Assets</h3>
        <p className="text-sm text-muted-foreground mt-0.5">
          Choose how this ticket associates with project assets.
        </p>
      </div>

      {/* MODE CARDS */}
      <Controller
        name="linkingMode"
        control={control}
        defaultValue={hasAnyItems ? 'single' : 'adhoc'}
        render={({ field }) => (
          <RadioGroup
            value={field.value}
            onValueChange={field.onChange}
            className="grid grid-cols-1 sm:grid-cols-3 gap-3"
          >
            {/* ── MULTI ── */}
            <ModeCard
              htmlFor="mode-multi"
              active={linkingMode === 'multi'}
              disabled={!hasAnyItems}
              disabledReason="No project assets available"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem
                  value="multi"
                  id="mode-multi"
                  disabled={!hasAnyItems}
                />
                <div className="p-1.5 rounded-md bg-primary/10">
                  <Layers className="w-3.5 h-3.5 text-primary" />
                </div>
                <span className="text-sm font-medium text-foreground">
                  Bulk
                </span>
              </div>
              <p className="text-xs text-muted-foreground pl-6">
                One ticket per matching item.
              </p>
              {linkingMode === 'multi' && (
                <div className="pl-6 flex items-center justify-between mt-1">
                  <span className="text-xs font-medium text-primary">
                    {linkedItems?.length ?? 0} selected
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowPreview(true);
                    }}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Eye className="w-3 h-3" /> Review
                  </button>
                </div>
              )}
            </ModeCard>

            {/* ── SINGLE ── */}
            <ModeCard
              htmlFor="mode-single"
              active={linkingMode === 'single'}
              disabled={!hasAnyItems}
              disabledReason="No project assets available"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem
                  value="single"
                  id="mode-single"
                  disabled={!hasAnyItems}
                />
                <div className="p-1.5 rounded-md bg-blue-500/10">
                  <Link2 className="w-3.5 h-3.5 text-blue-500" />
                </div>
                <span className="text-sm font-medium text-foreground">
                  Single
                </span>
              </div>
              <p className="text-xs text-muted-foreground pl-6">
                Link one asset or address.
              </p>
              {linkingMode === 'single' && hasAnyItems && (
                <div className="pl-6 mt-1 space-y-2">
                  {selectedSingleItem ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <div
                          className="w-5 h-5 rounded flex items-center justify-center shrink-0"
                          style={
                            selectedSingleItem.color
                              ? {
                                  backgroundColor: `${selectedSingleItem.color}20`,
                                }
                              : undefined
                          }
                        >
                          {selectedSingleItem.type === 'premise' ? (
                            <Home className="w-3 h-3 text-blue-500" />
                          ) : (
                            <Box
                              className="w-3 h-3"
                              style={{
                                color: selectedSingleItem.color ?? '#f97316',
                              }}
                            />
                          )}
                        </div>
                        <span className="text-xs text-foreground font-medium truncate">
                          {selectedSingleItem.label}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setOpenAssetList(true)}
                        className="text-[11px] text-muted-foreground hover:text-primary transition-colors shrink-0 ml-2"
                      >
                        Change
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setOpenAssetList(true)}
                      className="w-full h-7 text-xs border border-dashed border-border rounded-md hover:border-primary hover:text-primary text-muted-foreground transition-colors flex items-center justify-center gap-1.5"
                    >
                      <MapPin className="w-3 h-3" /> Select Item
                    </button>
                  )}
                </div>
              )}
            </ModeCard>

            {/* ── AD-HOC ── */}
            <ModeCard
              htmlFor="mode-adhoc"
              active={linkingMode === 'adhoc'}
              disabled={hasAnyItems}
              disabledReason="Not available — project assets exist"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem
                  value="adhoc"
                  id="mode-adhoc"
                  disabled={hasAnyItems}
                />
                <div className="p-1.5 rounded-md bg-amber-500/10">
                  <MapPin className="w-3.5 h-3.5 text-amber-500" />
                </div>
                <span className="text-sm font-medium text-foreground">
                  Ad-hoc
                </span>
              </div>
              <p className="text-xs text-muted-foreground pl-6">
                Link an external address only.
              </p>
            </ModeCard>
          </RadioGroup>
        )}
      />

      {/* ── MULTI FILTER ── */}
      {linkingMode === 'multi' && (
        <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-3 animate-in fade-in duration-200">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-foreground">
              Filter by type
            </p>
            <span className="text-xs text-muted-foreground">
              {linkedItems?.length ?? 0} items will be linked
            </span>
          </div>
          <FormSelect
            name="assetTypeFilter"
            label=""
            defaultValue={['ALL']}
            options={filterOptions}
            placeholder="Select item types..."
            multiple
          />
        </div>
      )}

      {/* ── AD-HOC ADDRESS ── */}
      {linkingMode === 'adhoc' && (
        <div className="rounded-xl border border-border bg-muted/20 p-4 animate-in fade-in duration-200">
          <AddressSection methods={methods} verifiedAddress={false} />
        </div>
      )}

      {/* ── SINGLE SELECT MODAL ── */}
      {openAssetList && linkingMode === 'single' && (
        <VirtualizedList
          open={openAssetList}
          onOpenChange={setOpenAssetList}
          items={allItems}
          getKey={(i) => i.id}
          getCategory={(i) => (i.type === 'premise' ? 'Addresses' : 'Assets')}
          renderItem={renderListItem}
          getLabel={(i) => i.label}
          rowHeight={60}
          isSelectable
          selectionMode="single"
          selectedIds={linkedItems?.map((i: any) => i.id)}
          onSelectionChange={handleSingleSelection}
        />
      )}

      {/* ── MULTI PREVIEW MODAL ── */}
      {showPreview && linkingMode === 'multi' && (
        <VirtualizedList
          open={showPreview}
          onOpenChange={setShowPreview}
          modalTitle={`${previewItems.length} tickets will be created`}
          items={previewItems}
          getKey={(i) => i.id}
          renderItem={renderListItem}
          getLabel={(i) => i.label}
          getCategory={(i) => (i.type === 'premise' ? 'Addresses' : 'Assets')}
          rowHeight={60}
          isSelectable={false}
        />
      )}
    </section>
  );
};

// =========================================================================
// MODE CARD
// =========================================================================

const ModeCard = ({
  htmlFor,
  active,
  disabled,
  disabledReason,
  children,
}: {
  htmlFor: string;
  active: boolean;
  disabled: boolean;
  disabledReason: string;
  children: React.ReactNode;
}) => (
  <label
    htmlFor={htmlFor}
    className={cn(
      'relative flex flex-col gap-2.5 p-3.5 rounded-xl border transition-all duration-150',
      // Active state
      active && !disabled
        ? 'border-border bg-card ring-1 ring-primary/30'
        : 'border-border bg-card',
      // Hover — only when not disabled
      !disabled && 'cursor-pointer hover:border-border/80 hover:bg-muted/20',
      // Disabled — greyed out, not hidden
      disabled && 'opacity-50 cursor-not-allowed',
    )}
  >
    {children}

    {/* Disabled reason banner */}
    {disabled && (
      <div className="flex items-center gap-1.5 mt-1 pt-2 border-t border-border bg-card">
        <AlertCircle className="w-3 h-3 text-muted-foreground shrink-0" />
        <span className="text-[10px] text-muted-foreground">
          {disabledReason}
        </span>
      </div>
    )}
  </label>
);
